import React, { useState } from "react";
import { Card, CardBody, CardHeader, CardTitle, Col, Table, Button } from "reactstrap";

/**
 * Reusable table for all Service report sections.
 *
 * Props:
 *   title        – card header title
 *   subtitle     – small text below the title, inside the card header
 *   columns      – array of { header, accessor, unit?(string), renderCell?(value, row) }
 *   data         – array of row objects
 *   wide         – full row width (xs=12) vs default (xs=12 lg=4)
 *   half         – half row width (xs=12 lg=6)
 *   initialLimit – number of rows shown initially; a "Show more" button reveals the rest
 *   theme        – optional color theme object (define all themes in Service.js → TABLE_THEMES)
 *   description  – plain text below table (what the table shows)
 *   summary      – bold stats line below table, e.g. "16 total: 9 returned, 7 unreturned"
 */
const ServiceReportTable = ({
    title,
    subtitle,
    columns,
    data,
    wide = false,
    half = false,
    initialLimit,
    theme = {},
    description,
    summary,
}) => {
    const [showAll, setShowAll] = useState(false);

    const {
        colorBackgroundHeader,
        colorTextHeader,
        colorBackgroundOdd,
        colorBackgroundEven,
        colorTextOdd,
        colorTextEven,
        colorSubtitle,
        colorDescription,
    } = theme;
    const colProps = wide ? { xs: 12 } : half ? { xs: 12, lg: 6 } : { xs: 12, lg: 4 };

    const visibleData = initialLimit && !showAll ? data?.slice(0, initialLimit) : data;
    const hasMore = initialLimit && data?.length > initialLimit;

    return (
        <Col {...colProps} className="mb-4">
            <Card>
                <CardHeader style={{
                    ...(colorBackgroundHeader ? { backgroundColor: colorBackgroundHeader } : {}),
                    ...(colorTextHeader       ? { color: colorTextHeader }               : {}),
                }}>
                    <CardTitle tag="h3" className="font-weight-bold">{title}</CardTitle>
                    {subtitle && (
                        <p className="mb-0" style={{ fontSize: "0.85rem", color: colorSubtitle || "#ffffff" }}>{subtitle}</p>
                    )}
                    {description && (
                        <p className="mb-2" style={{ fontSize: "0.8rem", color: colorDescription || "#ffffff" }}>{description}</p>
                    )}
                </CardHeader>
                <CardBody>
                    {!data || data.length === 0 ? (
                        <p className="text-center text-muted mb-0">No data available.</p>
                    ) : (
                        <>
                            <Table size="sm" style={{ tableLayout: "auto" }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: "2rem" }}>#</th>
                                        {columns.map((col) => (
                                            <th key={col.accessor}>
                                                {col.header}{col.unit ? ` (${col.unit})` : ""}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleData.map((row, i) => {
                                        const rowColor = i % 2 === 0 ? colorTextOdd : colorTextEven;
                                        return (
                                            <tr
                                                key={i}
                                                style={{ backgroundColor: i % 2 === 0 ? colorBackgroundOdd : colorBackgroundEven }}
                                            >
                                                <td><span className="text-muted" style={{ color: rowColor }}>{i + 1}</span></td>
                                                {columns.map((col) => (
                                                    <td key={col.accessor}>
                                                        <span style={{ color: rowColor }}>
                                                            {col.renderCell
                                                                ? col.renderCell(row[col.accessor], row)
                                                                : col.unit
                                                                    ? `${row[col.accessor]}${col.unit}`
                                                                    : row[col.accessor]}
                                                        </span>
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>

                            {hasMore && (
                                <Button
                                    size="sm"
                                    color="secondary"
                                    className="btn-fill mt-1"
                                    onClick={() => setShowAll((prev) => !prev)}
                                >
                                    {showAll
                                        ? "Show less"
                                        : `Show more (${data.length - initialLimit} more)`}
                                </Button>
                            )}

                            {summary && (
                                <p className="mt-1 mb-0" style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                    {summary}
                                </p>
                            )}
                        </>
                    )}
                </CardBody>
            </Card>
        </Col>
    );
};

export default ServiceReportTable;
